import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Input,
  Textarea,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '@insolech/ui';
import { Upload, X, Minus, Plus } from 'lucide-react';
import {
  orderMutations,
  orderQueries,
  orderFileTypes,
  patientQueries,
  type OrderDetail, emailNotifications } from '@insolech/sdk';
import { orderFileIcon } from './orderFileIcon';
import { errorMessage } from '@/lib/errorMessage';
import { supabase } from '@/lib/supabase';
import { formatPromisedDate } from './useAnnouncedDateGuard';
import { useAuth } from '@/features/auth/AuthContext';
import { resolvePatientDisplay } from './productLabel';
import { isSandalLine } from './isSandalLine';
import {
  canEditOrderAtStatus,
  isInProductionOrLater,
  IN_PRODUCTION_WARNING,
} from './orderEditMatrix';
import {
  PrescriptionForm,
  buildLineConfiguration,
  type PrescriptionFormState,
} from './wizard/PrescriptionForm';
import {
  hydratePrescriptionFormState,
  isSolutionSubtype,
} from './prescriptionHydration';
import {
  SolutionSubtypePicker,
  type SolutionSubtype,
} from './wizard/SolutionSubtypePicker';
import { sanitizeTopCoverColorForSubtype } from './wizard/prescription/InsoleDesignSection';
import {
  computeExpectedDeliveryDate,
  formatDateOnly,
} from './deliveryDate';
import {
  collectPrescriptionMissingFields,
  type MissingField,
} from './wizard/prescriptionValidation';
import { evaThicknessProblem } from './wizard/prescription/evaThicknessRules';
import {
  QUANTITY_MIN,
  QUANTITY_MAX,
  clampQuantity,
  parseQuantityInput,
  quantityEditBlockedReason,
  labSheetAlreadyOut,
  labSheetStaleWarning,
  recomputeFailureMessage,
} from './orderLineQuantity';

/**
 * EditPrescriptionDialog
 *
 * Opens from OrderDetailPage's "Edit prescription" button. Lets the
 * podiatrist (or admin) change the solution_subtype, adjust any
 * prescription field, and attach additional files to a line that is
 * already past `draft`. Status gate (draft + submitted) is enforced by
 * the caller; the dialog itself only handles state + save.
 *
 * State model: we deep-clone the existing line.configuration + a
 * derived PrescriptionFormState into local React state on open. Cancel
 * discards by closing the modal (local state is recreated next time).
 * "Update prescription" rebuilds the configuration via
 * `buildLineConfiguration` and patches order_lines via the new
 * `updateOrderLineConfiguration` SDK helper.
 *
 * Files staged in the drop zone are uploaded sequentially after the
 * line patch succeeds, so a save failure on the line never strands a
 * partial file in storage.
 */

type Line = OrderDetail['lines'][number];

interface Props {
  open: boolean;
  order: OrderDetail;
  line: Line;
  onClose: () => void;
  /** Same visibility rule the lines card uses for money: the quantity
   *  section shows the unit x quantity = total preview only when true. */
  canViewPrices?: boolean;
}

interface StagedFile {
  localId: string;
  file: File;
  fileKind: 'foot_scan' | 'photo' | 'prescription' | 'stl' | 'additional';
  progress?: number;
  error?: string;
}


/**
 * Canonical subtype -> configuration.material mapping. Mirrors the wizard's
 * default-seed in PrescriptionForm so that whenever the operator changes the
 * solution subtype in this dialog, the denormalised `material` cache on the
 * line's configuration JSONB is rewritten to match. Without this sync, the
 * tracker's Product column (and any other consumer that prefers material
 * over subtype) keeps showing the pre-edit label.
 *
 * Note: 'carbon_insole' maps to 'Carbon' (not 'Carbon Fibre') to stay
 * consistent with the wizard's seed and the CUSTOM_INSOLE_MATERIAL_LABELS
 * lookup keys in productLabel.ts. Sandal / refurbishment subtypes have no
 * material concept and are deliberately omitted.
 */
/**
 * Subtype -> the denormalised `configuration.material` the SKU mapper keys on.
 *
 * EXPORTED so the wizard uses this exact table rather than a second copy. Two
 * copies of a money-path lookup is how they drift, and this one already had the
 * asymmetry that caused backlog item 6b: the dialog re-derived material on a
 * subtype change and the wizard did not.
 */
export const SUBTYPE_TO_MATERIAL: Partial<Record<SolutionSubtype, string>> = {
  pa12_insole: 'PA12',
  tpu_insole: 'TPU',
  eva_insole: 'EVA',
  polypropylene_insole: 'Polypropylene',
  fibreglass_insole: 'Fibreglass',
  carbon_insole: 'Carbon',
};

type FootUnit = 'EU' | 'UK' | 'US';
const FOOT_UNITS: ReadonlyArray<FootUnit> = ['EU', 'UK', 'US'];
const ORTHOTIC_USES: ReadonlyArray<string> = [
  'everyday_comfort',
  'sports',
  'stability',
  'dress_low_profile',
  'ucbl_kids',
  'emirates_cabin_crew',
];
/** patients.gender CHECK set; the RPC rejects anything else with 22023. */
const KNOWN_GENDERS: ReadonlyArray<string> = ['male', 'female', 'other', 'prefer_not_to_say'];
/** update_order_patient_gender returned { synced: false, note: null }: a
 *  record-less line, so the value went onto this order's configuration. */
const RECORDLESS_GENDER_NOTE =
  'Saved on this order only; there is no patient record to update.';

interface PatientDetailsState {
  name: string;
  /** patients.gender, or configuration.gender for a record-less order; ''
   *  when neither is stored. Saved through update_order_patient_gender. */
  gender: string;
  age: string;
  weightKg: string;
  leftFootSize: string;
  leftFootUnit: FootUnit;
  rightFootSize: string;
  rightFootUnit: FootUnit;
  shoeBrand: string;
  orthoticIntendedUse: string;
  /** patients.notes, the free text the podiatrist wrote about the patient. */
  notes: string;
}

/**
 * Hydrate the editable patient-details fields. Foot sizes / weight / intended
 * use / shoe brand live on the line's configuration (order-scoped, already
 * matrix-gated by order_lines_update RLS). The name resolves from the canonical
 * patients record OR the line free-text (legacy patient_id NULL orders). Age is
 * read from configuration.age, falling back to the patient DOB for display.
 */
function hydratePatientDetails(order: OrderDetail, line: Line): PatientDetailsState {
  const cfg = (line.configuration ?? {}) as Record<string, unknown>;
  const foot = (key: string): { value: string; unit: FootUnit } => {
    const node = cfg[key] as { value?: unknown; unit?: unknown } | undefined;
    const unit: FootUnit = node?.unit === 'UK' || node?.unit === 'US' ? node.unit : 'EU';
    return { value: node?.value != null ? String(node.value) : '', unit };
  };
  const left = foot('left_foot_size');
  const right = foot('right_foot_size');
  const name = resolvePatientDisplay({ patient: order.patient, lines: [line] }).name ?? '';
  let age = cfg.age != null ? String(cfg.age) : '';
  if (!age && order.patient?.date_of_birth) {
    const birthYear = new Date(order.patient.date_of_birth).getFullYear();
    if (birthYear > 1900) age = String(new Date().getFullYear() - birthYear);
  }
  return {
    name,
    // Same precedence as the Excel exporter and the detail page: the record,
    // then the line (record-less reorders persist gender on the config).
    gender: order.patient?.gender ?? (typeof cfg.gender === 'string' ? cfg.gender : ''),
    age,
    weightKg: cfg.weight_kg != null ? String(cfg.weight_kg) : '',
    leftFootSize: left.value,
    leftFootUnit: left.unit,
    rightFootSize: right.value,
    rightFootUnit: right.unit,
    shoeBrand: cfg.shoe_brand != null ? String(cfg.shoe_brand) : '',
    orthoticIntendedUse: cfg.orthotic_intended_use != null ? String(cfg.orthotic_intended_use) : '',
    // Read in the SAME order prescriptionExcelExport reads it, so what is shown
    // here is what prints on the lab sheet. patients.notes is the single source
    // of truth (see OrderDetail.patient.notes in the SDK); the config keys are
    // the documented fallback for legacy record-less orders (patient_id NULL).
    notes:
      order.patient?.notes ??
      (cfg.patient_notes != null
        ? String(cfg.patient_notes)
        : cfg.patient_observations != null
          ? String(cfg.patient_observations)
          : ''),
  };
}

// hydratePrescriptionFormState + extractModifications now live in the shared
// ./prescriptionHydration module so this dialog and the wizard's draft-resume
// use one transform. hydratePatientDetails stays local (it builds this
// dialog's own PatientDetailsState, not wizard state).

function cryptoId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2, 12);
}

export function EditPrescriptionDialog({
  open,
  order,
  line,
  onClose,
  canViewPrices = false,
}: Props) {
  const { user, isAdmin } = useAuth();
  const qc = useQueryClient();

  // Snapshot the original subtype and form state on open. We re-snapshot
  // when the dialog actually transitions from closed -> open by keying
  // local state on `open`. (Modal unmounts the children when closed via
  // the conditional render in the caller, so this is also handled there,
  // but the key-driven reset belt-and-braces the case where a parent
  // keeps it mounted.)
  const initialSubtype: SolutionSubtype | null = isSolutionSubtype(line.solution_subtype)
    ? line.solution_subtype
    : null;

  const [subtype, setSubtype] = React.useState<SolutionSubtype | null>(initialSubtype);
  // Orthotic intended use + shoe brand describe the shoe an INSOLE goes into:
  // hidden and never written for a sandal (same rule as the wizard's patient
  // step and orthoticFieldsForLine). Follows the subtype being EDITED, so a
  // sandal converted to an insole in this dialog shows the two fields for that
  // save; the stored line only decides when no subtype is known. Keys an old
  // sandal line already stores are left untouched, because mergedConfig starts
  // from the stored config.
  const isSandal = subtype ? subtype === 'orthopaedic_sandal' : isSandalLine(line);
  // Changing the solution type RECALCULATES expected delivery on save, which
  // silently re-dates an order a podiatrist may already have been promised.
  // Fetch what they were told when the dialog opens, so the notice can sit
  // next to the picker rather than arriving after the fact.
  const [announced, setAnnounced] =
    React.useState<emailNotifications.DeliveryAnnouncementState | null>(null);
  const [form, setForm] = React.useState<PrescriptionFormState>(() =>
    hydratePrescriptionFormState(line),
  );
  const [patient, setPatient] = React.useState<PatientDetailsState>(() =>
    hydratePatientDetails(order, line),
  );
  const originalPatientName = React.useRef(patient.name);
  const originalPatientGender = React.useRef(patient.gender);
  const [stagedFiles, setStagedFiles] = React.useState<StagedFile[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  // Non-error notice held open after a successful save (e.g. the patient name
  // was changed for this order but a shared/foreign patients record was left
  // unchanged - surfaced from the RPC's `note`).
  const [saveNotice, setSaveNotice] = React.useState<string | null>(null);
  // Quantity (pairs) as TEXT so typing stays free while parsing stays
  // digits-only; parseQuantityInput is the single source of the number.
  const [quantityText, setQuantityText] = React.useState<string>(String(line.quantity ?? 1));

  // Patient-detail edit gate mirrors the server matrix (admin any status;
  // clinic podiatrist only while order + line are draft/submitted). The name
  // RPC and order_lines_update RLS are authoritative; this only shapes the UI.
  const canEditPatient = canEditOrderAtStatus(isAdmin, order.status, line.status);
  const showProdWarning = isAdmin && isInProductionOrLater(order.status);

  // Quantity rules (money): same matrix gate as the other fields, PLUS the
  // invoiced/paid boundary - once money is documented or collected, a
  // correction is a credit note, not an edit, for admins too (UI-level; the
  // durable DB freeze is a backlog item blocked on invoice stamping).
  const originalQuantity = line.quantity ?? 1;
  const parsedQuantity = parseQuantityInput(quantityText);
  const quantityChanged = parsedQuantity !== null && parsedQuantity !== originalQuantity;
  const quantityBlockedReason = quantityEditBlockedReason(order);
  const canEditQuantity = canEditPatient && !quantityBlockedReason;
  // Non-blocking lab-sheet warning. Keyed on LINE STATUS because no stored
  // marker for "Excel sent / Gmail draft created" exists anywhere
  // (gmail-create-draft writes no tables).
  const showLabSheetWarning = quantityChanged && labSheetAlreadyOut(line.status);

  // Reset state every time the dialog opens against a (potentially) new
  // line. Without this, re-opening after a save would still show the
  // pre-save snapshot until the page refetched the order detail.
  React.useEffect(() => {
    if (!open) return;
    setAnnounced(null);
    void emailNotifications
      .fetchDeliveryAnnouncementState(supabase, order.id)
      .then(setAnnounced)
      // Admin-only RPC: a podiatrist editing their own prescription gets a
      // 42501 here, which simply means no notice for them.
      .catch(() => setAnnounced(null));
    setSubtype(initialSubtype);
    setForm(hydratePrescriptionFormState(line));
    const nextPatient = hydratePatientDetails(order, line);
    setPatient(nextPatient);
    originalPatientName.current = nextPatient.name;
    originalPatientGender.current = nextPatient.gender;
    setStagedFiles([]);
    setQuantityText(String(line.quantity ?? 1));
    setSaveError(null);
    setSaveNotice(null);
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line.id]);

  // Drop zone handlers
  const onPickFiles = (files: FileList | null) => {
    if (!files) return;
    const newOnes: StagedFile[] = [];
    for (let i = 0; i < files.length; i += 1) {
      const f = files.item(i);
      if (!f) continue;
      // Validate type + size at pick time; a rejection is shown inline, never
      // silently dropped.
      const v = orderFileTypes.validateOrderFile(f);
      newOnes.push({
        localId: cryptoId(),
        file: f,
        fileKind: 'additional',
        ...(v.ok ? {} : { error: v.reason }),
      });
    }
    setStagedFiles((prev) => [...prev, ...newOnes]);
  };

  const onRemoveStaged = (id: string) =>
    setStagedFiles((prev) => prev.filter((f) => f.localId !== id));

  // Real-time required-field check. Re-runs on every render because
  // `form` + `subtype` are React state; clearing the last missing field
  // immediately re-enables "Update prescription".
  const missingFields: MissingField[] = React.useMemo(
    () => collectPrescriptionMissingFields(form, subtype),
    [form, subtype],
  );
  const hasMissingFields = missingFields.length > 0;

  const onSave = async () => {
    setSaveError(null);
    setSaveNotice(null);
    if (!user?.id) {
      setSaveError('You must be signed in.');
      return;
    }
    // Belt-and-braces: the button is disabled when fields are missing,
    // but guard the handler too in case it's invoked programmatically.
    if (hasMissingFields) {
      setSaveError(
        `Please complete: ${missingFields.map((f) => f.label).join(', ')}.`,
      );
      return;
    }
    // EVA thickness is a free number (2 to 6 mm, at most one decimal). The
    // wizard blocks submit on it through checkUnavailableValues. This dialog
    // renders PrescriptionForm, so collectUnavailableValues already runs here
    // (it feeds the form's banner); what this save has never consulted is the
    // GATE, checkUnavailableValues, and wiring it in now would start blocking
    // the 57 live carbon 2.0mm lines on an unrelated edit. So the one numeric
    // rule is guarded here on its own. Same message as the inline one under
    // the input, and the SDK is not called: a typed 2.55, or a 7 stored
    // before the rule, must be corrected, never saved through or silently
    // altered.
    if (subtype === 'eva_insole') {
      const thicknessProblem = evaThicknessProblem(form.deep?.design?.eva_thickness_mm);
      if (thicknessProblem) {
        setSaveError(thicknessProblem);
        return;
      }
    }
    setSaving(true);
    try {
      const nextConfig = buildLineConfiguration(form, { subtype });
      // Preserve patient metrics already on the persisted configuration
      // (weight_kg, foot sizes, etc.) — buildLineConfiguration only
      // marshals the form fields, but the edit dialog doesn't expose
      // those metrics, so we don't want to drop them on save.
      const existingConfig = (line.configuration ?? {}) as Record<string, unknown>;
      const mergedConfig: Record<string, unknown> = { ...existingConfig, ...nextConfig };
      // If the subtype changed, drop any persisted size that came from a
      // legacy shape and let the rebuilt size win - buildLineConfiguration
      // already emits the correct shape under `size`.
      if (nextConfig.size) mergedConfig.size = nextConfig.size;
      if (nextConfig.deep) mergedConfig.deep = nextConfig.deep;
      // W2 write-side guard: force configuration.material to match the
      // final subtype before persisting. The onChange wrapper on the
      // SolutionSubtypePicker already keeps form.material in sync, but
      // we re-derive here so even programmatic state paths cannot leave
      // a stale material cached on the DB row.
      if (subtype) {
        const mapped = SUBTYPE_TO_MATERIAL[subtype];
        if (mapped) mergedConfig.material = mapped;
      }

      // Fold the editable patient metrics (order-scoped) into the SAME single
      // configuration write so there is no second, clobbering config update.
      // These keys are what the Excel export + workshop PDF read, so a
      // correction flows straight through on re-generation.
      if (canEditPatient) {
        if (patient.leftFootSize.trim()) {
          mergedConfig.left_foot_size = { value: patient.leftFootSize.trim(), unit: patient.leftFootUnit };
        }
        if (patient.rightFootSize.trim()) {
          mergedConfig.right_foot_size = { value: patient.rightFootSize.trim(), unit: patient.rightFootUnit };
        }
        const w = Number(patient.weightKg);
        if (patient.weightKg.trim() && Number.isFinite(w) && w > 0) mergedConfig.weight_kg = w;
        if (!isSandal) {
          if (patient.shoeBrand.trim()) mergedConfig.shoe_brand = patient.shoeBrand.trim();
          if (patient.orthoticIntendedUse.trim()) {
            mergedConfig.orthotic_intended_use = patient.orthoticIntendedUse.trim();
          }
        }
        const a = Number(patient.age);
        if (patient.age.trim() && Number.isFinite(a) && a > 0) mergedConfig.age = a;
        // Patient notes, but ONLY for a record-less order.
        //
        // Written to exactly ONE place, never both, because the exporter reads
        // `order.patient?.notes ?? config.patient_notes` and writing both would
        // let them diverge: a failed patients update would leave the new text in
        // the config and the OLD text on the lab sheet, which is the worst
        // outcome available. When a patients record exists it is the single
        // source of truth and is updated below instead.
        if (!order.patient?.id) {
          mergedConfig.patient_notes = patient.notes.trim();
        }
      }

      const subtypeChanged = subtype !== initialSubtype;
      // Recompute expected delivery only if the subtype actually changed.
      // Multi-line orders aren't supported on the edit dialog (single-line-
      // per-order is the design contract), so we just use this one line's
      // new subtype.
      let nextExpectedDelivery: string | null | undefined = undefined;
      if (subtypeChanged) {
        const computed = computeExpectedDeliveryDate(new Date(), [subtype]);
        nextExpectedDelivery = computed ? formatDateOnly(computed) : null;
      }

      await orderMutations.updateOrderLineConfiguration(supabase, {
        orderLineId: line.id,
        orderId: order.id,
        line_type: form.line_type,
        solution_subtype: subtype,
        product_id: form.product_id ?? null,
        configuration: mergedConfig,
        modifications: form.modifications,
        ...(nextExpectedDelivery !== undefined
          ? { expected_delivery_date: nextExpectedDelivery }
          : {}),
        // Quantity rides only when the user changed it AND the gate + the
        // invoiced/paid boundary allow it. Providing it makes the SDK run
        // recompute_order_totals right after the line patch; a recompute
        // failure surfaces as TotalsRecomputeFailedError in the catch
        // below and the dialog MUST NOT close green over stale totals.
        ...(quantityChanged && canEditQuantity && parsedQuantity !== null
          ? { quantity: parsedQuantity }
          : {}),
      });

      // Patient NAME lives outside the line config. The RPC always renames the
      // order's own name and syncs the shared patients record only when it is
      // this order's own (same clinic, not shared); otherwise it returns a note
      // we surface. Route it through the status-gated RPC, only when it changed.
      let nameNote: string | null = null;
      if (canEditPatient && patient.name.trim() !== originalPatientName.current.trim()) {
        const res = await orderMutations.updateOrderPatientName(supabase, {
          orderId: order.id,
          lineId: line.id,
          name: patient.name.trim(),
        });
        nameNote = res.note;
      }

      // Patient GENDER, gated exactly like the name: same matrix, only when it
      // changed, and never an empty value (the RPC rejects one with 22004; the
      // select only offers "Not specified" while nothing is stored). The RPC
      // updates patients.gender when the record is this order's own (that
      // propagates to the patient's other orders, intended) and otherwise
      // writes configuration.gender on the line and explains why in `note`.
      let genderNote: string | null = null;
      const nextGender = patient.gender.trim();
      if (canEditPatient && nextGender && nextGender !== originalPatientGender.current.trim()) {
        const res = await orderMutations.updateOrderPatientGender(supabase, {
          orderId: order.id,
          lineId: line.id,
          gender: nextGender,
        });
        // A null note with synced=false is the record-less case: say so, the
        // same way the other notes explain where the value went.
        genderNote = res.note ?? (res.synced ? null : RECORDLESS_GENDER_NOTE);
      }
      // Both RPCs may leave a shared/foreign record unchanged; show every note.
      const patientNote = [nameNote, genderNote].filter((n): n is string => !!n).join(' ') || null;

      // Patient notes for a RECORD-BACKED order. patients.notes is patient-
      // scoped by design, not order-scoped: the note is about the person, and
      // the SDK contract calls it the single source of truth that flows into any
      // future regenerated Excel. So this edit is visible on the patient's other
      // orders too, which is correct and worth knowing.
      //
      // Only written when it actually CHANGED. The wizard's rule (NewOrderPage's
      // `notesLoaded` guard) is that a box which started blank must never be
      // allowed to overwrite stored notes; here the box is always hydrated from
      // the loaded record, so an unchanged value simply omits the write.
      //
      // NOT wrapped in try/catch: RLS restricts patients UPDATE to members of
      // the patient's own clinic, so a cross-clinic attempt SHOULD fail, and it
      // must fail loudly. Swallowing it would tell the user their note was saved
      // when the lab sheet will still show the old one.
      if (
        canEditPatient &&
        order.patient?.id &&
        patient.notes.trim() !== (order.patient.notes ?? '').trim()
      ) {
        await patientQueries.updatePatient(supabase, order.patient.id, {
          notes: patient.notes.trim() || null,
        });
      }

      // Upload any newly staged files. Failures don't roll back the
      // prescription patch, but we count them so we can KEEP THE DIALOG OPEN
      // and tell the user (never a silent drop: a closed dialog would hide the
      // inline error). Files flagged invalid at pick time also count.
      let fileFailures = 0;
      if (stagedFiles.length > 0 && order.clinic_id) {
        for (const sf of stagedFiles) {
          if (sf.error) {
            fileFailures += 1;
            continue;
          }
          try {
            await orderMutations.uploadOrderFile(supabase, {
              bucket: sf.fileKind === 'prescription' ? 'prescriptions' : 'scans',
              clinicId: order.clinic_id,
              orderId: order.id,
              orderLineId: line.id,
              file: sf.file,
              fileKind: sf.fileKind,
              uploaderId: user.id,
              onProgress: (p) =>
                setStagedFiles((prev) =>
                  prev.map((f) => (f.localId === sf.localId ? { ...f, progress: p } : f)),
                ),
            });
          } catch (e) {
            fileFailures += 1;
            setStagedFiles((prev) =>
              prev.map((f) =>
                f.localId === sf.localId
                  ? { ...f, error: e instanceof Error ? e.message : String(e) }
                  : f,
              ),
            );
          }
        }
      }

      // Refresh detail + files + history (status didn't change but a
      // future audit-trail hook may emit an "edited" event). W5: also
      // invalidate the orders list root so the tracker's Product column
      // re-renders when subtype / material / product_id change; without
      // this the tracker shows the stale label until another mutation
      // (status change, navigation, etc.) refreshes the list.
      await Promise.all([
        qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.detail(order.id) }),
        qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.files(order.id) }),
        qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.history(order.id) }),
        qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.all }),
        // The name, gender and notes edits above may have written the shared
        // patients record; the patient list/detail must not wait out staleTime.
        qc.invalidateQueries({ queryKey: patientQueries.patientsKeys.all }),
      ]);
      if (fileFailures > 0) {
        // Prescription + patient details already saved; hold the dialog open so
        // the per-file errors below stay visible and the user can retry.
        setSaveError(
          `The prescription was saved, but ${fileFailures} document(s) could not be attached. See the file list below; remove or re-add them, then save again.`,
        );
        // A name/gender note reflects an already-committed edit; surface it too
        // so it isn't lost behind the file-failure message.
        if (patientNote) setSaveNotice(patientNote);
        setSaving(false);
        return;
      }
      if (patientNote) {
        // Saved successfully, but the shared/foreign patients record was left
        // unchanged - hold the dialog open so the user sees why.
        setSaveNotice(patientNote);
        setSaving(false);
        return;
      }
      onClose();
    } catch (e) {
      // Stale-totals is its own, louder failure: the line update already
      // landed but recompute_order_totals did not, so total_aed is wrong
      // everywhere until a retry succeeds. Pressing Save again is a safe
      // retry (idempotent line patch + server-side re-sum).
      setSaveError(recomputeFailureMessage(e) ?? (errorMessage(e) || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <ModalContent size="xl">
        <ModalHeader>
          <ModalTitle>
            Edit prescription · {order.order_number} · Line {line.line_number}
          </ModalTitle>
        </ModalHeader>
        <ModalBody className="max-h-[70vh] overflow-y-auto space-y-4">
          {saveError ? (
            <Alert tone="error" title="Could not save prescription">
              {saveError}
            </Alert>
          ) : null}

          {saveNotice ? (
            <Alert tone="info" title="Saved — one note">
              {saveNotice}
            </Alert>
          ) : null}

          {isAdmin ? null : (
            <Alert tone="info" title="Editing a submitted order">
              Changes here are saved directly to the order. If production has
              already started, additional follow-up with your INSOLECH provider
              may be needed.
            </Alert>
          )}

          {showProdWarning ? (
            <Alert tone="warning" title="Order already in production">
              {IN_PRODUCTION_WARNING}
            </Alert>
          ) : null}

          {/* Patient details. Foot sizes / weight / intended use / shoe brand
              / age are order-scoped (saved onto the line configuration on the
              existing matrix-gated path); the name goes through the status-
              gated update_order_patient_name RPC. Read-only when the matrix
              blocks edits (podiatrist past submitted). */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-text-primary">Patient details</h3>
            {!canEditPatient ? (
              <p className="text-xs text-text-muted">
                This order has moved to production; patient details are read-only.
              </p>
            ) : null}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block space-y-1">
                <span className="text-xs text-text-muted">Patient name</span>
                <Input
                  value={patient.name}
                  disabled={!canEditPatient}
                  onChange={(e) => setPatient((p) => ({ ...p, name: e.target.value }))}
                />
              </label>
              {/* Gender, saved through update_order_patient_gender on the same
                  gate as the name. "Not specified" is offered only while
                  nothing is stored (the RPC refuses an empty value), and a
                  stored 'other' / 'prefer_not_to_say' keeps its option so a
                  save without touching the field never clobbers it. */}
              <label className="block space-y-1">
                <span className="text-xs text-text-muted">Gender</span>
                <select
                  className="h-9 w-full rounded-md border border-border-default bg-surface-elevated px-2 text-sm text-text-primary disabled:opacity-50"
                  value={patient.gender}
                  disabled={!canEditPatient}
                  onChange={(e) => setPatient((p) => ({ ...p, gender: e.target.value }))}
                  data-testid="edit-patient-gender"
                >
                  {patient.gender === '' ? <option value="">Not specified</option> : null}
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  {patient.gender === 'other' ? <option value="other">Other</option> : null}
                  {patient.gender === 'prefer_not_to_say' ? (
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  ) : null}
                  {/* A stored value outside the CHECK set (legacy 'M' on a
                      line config) would otherwise paint as "Male" and be
                      saved as such on the next untouched save. */}
                  {patient.gender && !KNOWN_GENDERS.includes(patient.gender) ? (
                    <option value={patient.gender}>{patient.gender}</option>
                  ) : null}
                </select>
              </label>
              <label className="block space-y-1">
                <span className="text-xs text-text-muted">Age</span>
                <Input
                  inputMode="numeric"
                  value={patient.age}
                  disabled={!canEditPatient}
                  onChange={(e) => setPatient((p) => ({ ...p, age: e.target.value }))}
                />
              </label>
              <div className="space-y-1">
                <span className="text-xs text-text-muted">Left foot size</span>
                <div className="flex gap-2">
                  <Input
                    className="flex-1"
                    placeholder="e.g. 42 or see scan"
                    value={patient.leftFootSize}
                    disabled={!canEditPatient}
                    onChange={(e) => setPatient((p) => ({ ...p, leftFootSize: e.target.value }))}
                  />
                  <select
                    className="h-9 rounded-md border border-border-default bg-surface-elevated px-2 text-sm text-text-primary disabled:opacity-50"
                    value={patient.leftFootUnit}
                    disabled={!canEditPatient}
                    onChange={(e) =>
                      setPatient((p) => ({ ...p, leftFootUnit: e.target.value as FootUnit }))
                    }
                    aria-label="Left foot size unit"
                  >
                    {FOOT_UNITS.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-xs text-text-muted">Right foot size</span>
                <div className="flex gap-2">
                  <Input
                    className="flex-1"
                    placeholder="e.g. 42 or see scan"
                    value={patient.rightFootSize}
                    disabled={!canEditPatient}
                    onChange={(e) => setPatient((p) => ({ ...p, rightFootSize: e.target.value }))}
                  />
                  <select
                    className="h-9 rounded-md border border-border-default bg-surface-elevated px-2 text-sm text-text-primary disabled:opacity-50"
                    value={patient.rightFootUnit}
                    disabled={!canEditPatient}
                    onChange={(e) =>
                      setPatient((p) => ({ ...p, rightFootUnit: e.target.value as FootUnit }))
                    }
                    aria-label="Right foot size unit"
                  >
                    {FOOT_UNITS.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="block space-y-1">
                <span className="text-xs text-text-muted">Weight (kg)</span>
                <Input
                  inputMode="decimal"
                  value={patient.weightKg}
                  disabled={!canEditPatient}
                  onChange={(e) => setPatient((p) => ({ ...p, weightKg: e.target.value }))}
                />
              </label>
              {!isSandal ? (
                <>
                  <label className="block space-y-1">
                    <span className="text-xs text-text-muted">Shoe brand</span>
                    <Input
                      value={patient.shoeBrand}
                      disabled={!canEditPatient}
                      onChange={(e) => setPatient((p) => ({ ...p, shoeBrand: e.target.value }))}
                    />
                  </label>
                  <label className="block space-y-1 sm:col-span-2">
                    <span className="text-xs text-text-muted">Orthotic intended use</span>
                    <select
                      className="h-9 w-full rounded-md border border-border-default bg-surface-elevated px-2 text-sm text-text-primary disabled:opacity-50"
                      value={patient.orthoticIntendedUse}
                      disabled={!canEditPatient}
                      onChange={(e) =>
                        setPatient((p) => ({ ...p, orthoticIntendedUse: e.target.value }))
                      }
                    >
                      <option value="">Not specified</option>
                      {ORTHOTIC_USES.map((u) => (
                        <option key={u} value={u}>
                          {u
                            .split('_')
                            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(' ')}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              ) : null}
              {/* Patient notes. Was missing entirely from this dialog, so the
                  free text a podiatrist wrote about the patient was visible on
                  the lab sheet and in the wizard but nowhere on the order the
                  admin actually opens. Full width because it is prose, not a
                  measurement. */}
              <label className="block space-y-1 sm:col-span-2">
                <span className="text-xs text-text-muted">Patient notes</span>
                <Textarea
                  rows={3}
                  value={patient.notes}
                  disabled={!canEditPatient}
                  onChange={(e) => setPatient((p) => ({ ...p, notes: e.target.value }))}
                  className="text-sm"
                  data-testid="edit-patient-notes"
                  placeholder="What the podiatrist wrote about this patient"
                />
                {order.patient?.id ? (
                  <span className="text-[11px] text-text-muted">
                    Saved on the patient record, so it applies to their other orders too.
                  </span>
                ) : (
                  <span className="text-[11px] text-text-muted">
                    This order has no patient record, so the note is saved on the order.
                  </span>
                )}
              </label>
            </div>
          </section>

          {/* Quantity (pairs). Money field: quantity multiplies unit price,
              so the stepper is bounded (1..10, digits only, no free text
              that can produce zero, a negative or a decimal), locked past
              the invoiced/paid boundary, and every change recomputes the
              header totals through recompute_order_totals on save. */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-text-primary">Quantity (pairs)</h3>
            {quantityBlockedReason ? (
              <p className="text-xs text-text-muted" data-testid="quantity-blocked-reason">
                {quantityBlockedReason}
              </p>
            ) : null}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                aria-label="Decrease quantity"
                data-testid="quantity-minus"
                disabled={!canEditQuantity || (parsedQuantity ?? QUANTITY_MIN) <= QUANTITY_MIN}
                onClick={() =>
                  setQuantityText(String(clampQuantity((parsedQuantity ?? originalQuantity) - 1)))
                }
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                inputMode="numeric"
                className="w-20 text-center"
                value={quantityText}
                disabled={!canEditQuantity}
                data-testid="quantity-input"
                aria-label="Quantity (pairs)"
                onChange={(e) => setQuantityText(e.target.value.replace(/\D/g, ''))}
                onBlur={() =>
                  setQuantityText(String(parseQuantityInput(quantityText) ?? originalQuantity))
                }
              />
              <Button
                variant="outline"
                size="icon"
                aria-label="Increase quantity"
                data-testid="quantity-plus"
                disabled={!canEditQuantity || (parsedQuantity ?? QUANTITY_MAX) >= QUANTITY_MAX}
                onClick={() =>
                  setQuantityText(String(clampQuantity((parsedQuantity ?? originalQuantity) + 1)))
                }
              >
                <Plus className="h-4 w-4" />
              </Button>
              {canViewPrices ? (
                <span className="text-sm text-text-muted" data-testid="quantity-price-preview">
                  AED {line.unit_price_aed.toFixed(2)} × {parsedQuantity ?? originalQuantity}{' '}
                  ={' '}
                  <span className="font-medium text-text-primary">
                    AED {(line.unit_price_aed * (parsedQuantity ?? originalQuantity)).toFixed(2)}
                  </span>
                </span>
              ) : null}
            </div>
            <p className="text-[11px] text-text-muted">
              Between {QUANTITY_MIN} and {QUANTITY_MAX} pairs. Saving recomputes the order totals.
            </p>
            {showLabSheetWarning ? (
              <Alert tone="warning" title="Lab sheet already sent">
                <span data-testid="quantity-labsheet-warning">
                  {labSheetStaleWarning(originalQuantity)}
                </span>
              </Alert>
            ) : null}
          </section>

          {/* Solution subtype picker - changing this triggers a delivery
              date recompute on save. We also sync form.material so the
              denormalised material on configuration matches the new family
              (write-side guard against the W2 staleness bug). */}
          {subtype !== initialSubtype && announced && announced.state !== 'never_announced' ? (
            <Alert
              tone={announced.state === 'window_closed' ? 'warning' : 'info'}
              title={
                announced.state === 'window_closed'
                  ? 'This will re-date an order the podiatrist was already promised'
                  : 'This will re-date an announced order'
              }
            >
              Changing the solution type recalculates the expected delivery date.{' '}
              {announced.podiatrist_name ?? 'The podiatrist'} was told this order arrives{' '}
              {formatPromisedDate(announced.promised_edd)}
              {announced.state === 'window_closed'
                ? '. That announcement can no longer be corrected automatically, so message them yourself after saving.'
                : ". Tonight's 21:00 correction will tell them the new date."}
            </Alert>
          ) : null}
          <SolutionSubtypePicker
            value={subtype}
            onChange={(next) => {
              setSubtype(next);
              if (next) {
                const mapped = SUBTYPE_TO_MATERIAL[next];
                if (mapped) {
                  setForm((prev) =>
                    prev.material === mapped ? prev : { ...prev, material: mapped },
                  );
                }
              }
              // Same shell-switch rule as the wizard: a Leather colour that
              // the new shell cannot produce is reset to Black at event time
              // (the saved configuration carries the STORED value, so the
              // render-time fallback alone is not enough).
              setForm((prev) => {
                const design = prev.deep?.design;
                const fixed = sanitizeTopCoverColorForSubtype(next, design);
                return fixed !== null && design
                  ? { ...prev, deep: { ...prev.deep, design: { ...design, top_cover_color: fixed } } }
                  : prev;
              });
            }}
          />

          {/* The full prescription form, editable. We pass the captured
              subtype so material defaults + product auto-pick follow the
              wizard's behaviour, and the edited patient name so the Insole
              Design section's "copy patient name" checkbox (with the Prophet-
              name warning) works here exactly as in the wizard. The full
              patient section is not mounted; our compact Patient details
              section above owns those fields. */}
          <PrescriptionForm
            value={form}
            onChange={setForm}
            readOnly={false}
            {...(subtype ? { subtype } : {})}
            patientName={patient.name}
            // The order's clinic, threaded to the prescription assistant so an
            // admin can save a preset under it (presets are clinic-owned and
            // an admin has no default clinic on useAuth).
            patientOrderClinicId={order.clinic_id}
            assistantCollapsed
          />

          {/* Additional documents drop zone. Reuses the single-zone pattern
              from FileUploadStep so podiatrists get the same UX as the new
              order wizard. Files upload sequentially after the line patch
              succeeds. */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-text-primary">Additional documents</h3>
            <p className="text-xs text-text-muted">
              PDF, Excel, CSV, Word, images, STL/OBJ/PLY, ZIP. Attached documents
              are included when the order is emailed to the supplier.
            </p>
            <UploadDropzone accept={orderFileTypes.ORDER_FILE_ACCEPT} onPick={onPickFiles} />
            {stagedFiles.length > 0 ? (
              <ul className="divide-y divide-border-subtle border border-border-subtle rounded-md">
                {stagedFiles.map((sf) => (
                  <FileRow
                    key={sf.localId}
                    file={sf}
                    onRemove={() => onRemoveStaged(sf.localId)}
                  />
                ))}
              </ul>
            ) : null}
          </section>
        </ModalBody>
        {hasMissingFields ? (
          <div className="px-6 pt-3">
            <Alert tone="warning" title="Required fields missing">
              <span>
                Please complete: {missingFields.map((f) => f.label).join(', ')}.
              </span>
            </Alert>
          </div>
        ) : null}
        <ModalFooter className="flex items-center justify-between gap-3">
          {/* Cancel & discard is never gated - the user must always have
              an exit hatch, even when the form is invalid. */}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel &amp; discard
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={saving || hasMissingFields || (canEditQuantity && parsedQuantity === null)}
            title={
              hasMissingFields
                ? `Missing: ${missingFields.map((f) => f.label).join(', ')}`
                : canEditQuantity && parsedQuantity === null
                  ? 'Enter a quantity between 1 and 10'
                  : undefined
            }
          >
            {saving ? 'Saving...' : 'Update prescription'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/*  Local file upload helpers                                                 */
/* -------------------------------------------------------------------------- */

function UploadDropzone({
  accept,
  onPick,
}: {
  accept: string;
  onPick: (files: FileList | null) => void;
}) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDragOver, setDragOver] = React.useState(false);

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onPick(e.dataTransfer.files);
      }}
      role="button"
      tabIndex={0}
      className={`flex flex-col items-center justify-center text-center p-6 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
        isDragOver
          ? 'border-brand-500 bg-brand-50'
          : 'border-border-subtle hover:border-brand-400 hover:bg-sand-50'
      }`}
    >
      <Upload className="h-6 w-6 text-text-muted mb-2" />
      <span className="text-sm font-medium text-text-primary">
        Upload or drag any relevant document
      </span>
      <span className="text-xs text-text-muted mt-1">
        STL, OBJ, PLY, JPG, PNG, PDF, up to 100 MB per file
      </span>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        onChange={(e) => onPick(e.target.files)}
        className="hidden"
      />
    </div>
  );
}

function FileRow({ file, onRemove }: { file: StagedFile; onRemove: () => void }) {
  const { Icon, label } = orderFileIcon(file.file.name, file.file.type);
  return (
    <li className="p-3 flex items-center gap-3">
      <div className="h-9 w-9 rounded-md bg-brand-50 text-brand-600 flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text-primary truncate">
            {file.file.name}
          </span>
          <span className="text-[11px] text-text-muted">{prettySize(file.file.size)}</span>
          <span className="text-[11px] uppercase tracking-wide text-text-muted">{label}</span>
        </div>
        {file.progress != null && file.progress < 1 ? (
          <div className="mt-1 h-1 w-full rounded-full bg-sand-100 overflow-hidden">
            <div
              className="h-full bg-brand-500 transition-all"
              style={{ width: `${Math.round((file.progress ?? 0) * 100)}%` }}
            />
          </div>
        ) : null}
        {file.error ? <p className="text-xs text-error-600 mt-1">{file.error}</p> : null}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onRemove}
        startIcon={<X className="h-4 w-4" />}
      >
        Remove
      </Button>
    </li>
  );
}

function prettySize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
